import i18n from 'i18next'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import { Favorite_PageIdEnum, Vehicle, VehicleAggregation } from 'src/api/gen/api'
import { DEPLOYED_STATUSES } from 'src/api/provider-agency'
import { useURLAndStorageState } from 'src/commons/hooks/useURLAndStorageState'
import { userHasPermission } from 'src/commons/permissions'
import { getJurisdictionTimezone, getTemporalRange, zonedDateTimeToISOString } from 'src/commons/temporalHelpers'
import { useAgencySettings } from 'src/layout/AgencySettings'
import { useProviders } from 'src/layout/hooks'
import { FilteringCard } from 'src/muiv5/components/commons/Cards/FilteringCard'
/*  FIXME (tlaurent 2023/08/10): I am not sure why we decided to use the SupervisionMap FilterPanel but it forces
 ** the VehicleList filters to have an empty areas and a null polygon
 */
import FilterPanel from 'src/pages/SupervisionMap/FilterPanel'

import { ISupervisionMapFilters } from '../SupervisionMap/types'

import VehiclesTable from './Table'

function getInitialFilters(): ISupervisionMapFilters {
  const range = getTemporalRange({ days: 9 }, true)
  return {
    identification_number: '',
    category: [],
    propulsion: [],
    status: DEPLOYED_STATUSES,
    provider: [],
    telemetryAfter: range.from,
    telemetryBefore: undefined,
    areas: [],
    polygon: null,
  }
}

function VehicleList(props: {
  storage_key?: Favorite_PageIdEnum.vehicles_list | Favorite_PageIdEnum.vehicles_list_standalone
}) {
  const { t } = useTranslation()

  const [devices, setDevices] = useState<Vehicle[]>([])
  const [devicesTotalCount, setDevicesTotalCount] = useState<number>(0)
  const [devicesFacets, setDevicesFacets] = useState<VehicleAggregation>({
    categories: {},
    statuses: {},
    propulsions: {},
    providers: {},
    total: 0,
  })
  const [isLoading, setLoading] = useState<boolean>(false)

  const { allowed_vehicle_types, vehicle_propulsions } = useAgencySettings()
  const defaultFilters = useMemo(getInitialFilters, [])
  const [filters, setFilters] = useURLAndStorageState(
    props.storage_key ?? Favorite_PageIdEnum.vehicles_list,
    defaultFilters,
    undefined,
    {
      telemetryAfter: defaultFilters.telemetryAfter,
      telemetryBefore: defaultFilters.telemetryBefore,
    }
  )
  const displayedFiltersRef = useRef<ISupervisionMapFilters>()

  const { activeProviders, activeProvidersIds } = useProviders()

  const fetchDataVehicles = async function (signal: AbortSignal) {
    setLoading(true)
    api.vehicles
      .vehiclesList({
        status: filters.status,
        type: filters.category,
        propulsion: filters.propulsion?.toString(),
        telemetry_before: filters.telemetryBefore ? zonedDateTimeToISOString(filters.telemetryBefore) : undefined,
        telemetry_after: filters.telemetryAfter ? zonedDateTimeToISOString(filters.telemetryAfter) : undefined,
        provider: filters.provider?.length ? filters.provider : activeProvidersIds,
        identification_number: filters.identification_number,
        options: { signal },
      })
      .then((vehicleResponse) => {
        if (!signal.aborted) {
          setDevicesFacets(vehicleResponse.aggregation)
          setDevices(vehicleResponse.results)
          setDevicesTotalCount(vehicleResponse.aggregation.total)
        }
      })
      .catch((reason) => {
        if (!signal.aborted) {
          console.error(reason)
        }
      })
      .finally(() => {
        if (!signal.aborted) {
          setLoading(false)
        }
      })
  }

  useEffect(() => {
    const abortController = new AbortController()
    fetchDataVehicles(abortController.signal)
    return () => abortController.abort()
  }, [filters])

  let onClickExport: ((event: React.MouseEvent<HTMLElement, MouseEvent>) => any) | undefined
  if (userHasPermission(['analyze', 'device'])) {
    onClickExport = async function () {
      await api.vehicles.vehiclesExport({
        status: filters.status,
        type: filters.category,
        propulsion: filters.propulsion?.toString(),
        telemetry_before: filters?.telemetryBefore ? zonedDateTimeToISOString(filters.telemetryBefore) : undefined,
        telemetry_after: filters?.telemetryAfter ? zonedDateTimeToISOString(filters.telemetryAfter) : undefined,
        provider: filters.provider && filters.provider.length ? filters.provider : activeProvidersIds,
        identification_number: filters.identification_number,
        // Export dates in the timezone and language of the user
        tz: getJurisdictionTimezone(),
        lang: i18n.language,
      })
    }
  }

  function onIdVinFilterChange(value: string) {
    setFilters((pendingFilters) => ({ ...pendingFilters, identification_number: value }))
  }

  function onApplyFilterClick() {
    if (!displayedFiltersRef.current) {
      throw new Error('There are no displayed filters')
    }

    setFilters(displayedFiltersRef.current)
  }

  return (
    <FilteringCard
      SideComponent={
        <FilterPanel
          ref={displayedFiltersRef}
          initialFilters={filters}
          defaultFilters={defaultFilters}
          providers={activeProviders}
          deviceCategories={allowed_vehicle_types ?? []}
          devicePropulsions={vehicle_propulsions}
          vehiclesCount={devicesTotalCount}
          vehiclesFacets={devicesFacets}
        />
      }
      onApplyFilter={onApplyFilterClick}
    >
      <VehiclesTable
        data={devices}
        isLoading={isLoading}
        totalCount={devicesTotalCount}
        onClickExport={onClickExport}
        searchBar={{
          value: filters.identification_number,
          onChange: onIdVinFilterChange,
          placeholder: t('vehicles.idOrVin'),
        }}
      />
    </FilteringCard>
  )
}

export default VehicleList
